import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { DEFAULT_REPORT_ORDER } from '@modeso/aumico-lib-annualreport-fe/constants/default-report-order';
import {
    AumicoInformationPopupComponent,
    DateLocalePipe,
    EMPTY_ARRAY_LENGTH,
    FIRST_ELEMENT_INDEX,
    IPopupData,
    MAT_DIALOG_CUSTOM_CLASS,
} from '@modeso/aumico-lib-core-fe';
import {
    IComplementaryReport,
    IDisplayOptions,
    IPrintOptions,
    ISignatureOptions,
    IStyleOptions,
} from '@modeso/types__aumico-lib-annualreport-be';
import { IReportSectionsTitles } from '@modeso/types__aumico-lib-annualreport-be/dist/models/interfaces/report-sections-titles.interface';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import Debug from 'debug';
import fastDeepEqual from 'fast-deep-equal/es6';
import { cloneDeep, isEqual, omit } from 'lodash-es';
import { IOpenedPrintOptionsAccordions } from 'projects/aumico-project-reports/src/app/interfaces/opened-print-options-accordions.interface';
import { AppStateDispatcher } from 'projects/aumico-project-reports/src/app/state/app.state.dispatcher';
import { distinctUntilChanged, filter } from 'rxjs/operators';
import { OrganizationSettings } from '../../../../../aumico-lib-users-fe/src/lib';
import { DEFAULT_SIGNATURE_OPTIONS } from '../../constants/default-signature-options';
import { DEFAULT_STYLE_OPTIONS } from '../../constants/default-style-options';
import { START_AT_PAGE_DEFAULT_VALUE } from '../../constants/default-swiss-display-options';
import { DEFAULT_SWISS_USER_DISPLAY_OPTIONS } from '../../constants/default-swiss-user-display-options';
import { localizationKeys } from '../../constants/localizationKeys';
import { IItemForCalculatingRatioOption } from '../../interfaces/IItemForCalculatingRatioOption.interface';
import { IUserDisplayOptions } from '../../interfaces/user-display-options.interface';
import { PrintOptionsVisibility } from '../../types/print-options-visibility.type';

const debug = Debug('aumico:aumico-lib-annualreport-fe:PrintOptionsComponent');
type ComplementaryReportsSelection = IDisplayOptions['complementaryReportsSelection'];
type ComplementaryReportSelectionItem = NonNullable<ComplementaryReportsSelection>[number];

@UntilDestroy()
@Component({
    selector: 'aumico-annualreports-print-options',
    templateUrl: 'print-options.component.html',
    styleUrls: ['print-options.component.scss'],
})
export class PrintOptionsComponent implements OnInit, OnChanges {
    private initialDisplayOptions: IUserDisplayOptions = { ...DEFAULT_SWISS_USER_DISPLAY_OPTIONS };
    private initialSignatureOptions: ISignatureOptions = { ...DEFAULT_SIGNATURE_OPTIONS };
    private readonly initialReportOrder: string[] = [...DEFAULT_REPORT_ORDER];
    private _previousDateTitleDefaultValue: string | undefined;
    private _currentDateTitleDefaultValue: string | undefined;
    private _printOptionsSavedData: IPrintOptions | undefined;
    private _complementaryReports: IComplementaryReport[] = [];

    public isDesignSettingsAccordionExpanded = false;
    public isComponentsAccordionExpanded = false;
    public isSettingsAccordionExpanded = false;
    public isSignatureAccordionExpanded = false;
    public isColorsAccordionExpanded = false;
    public isFxAccordionExpanded = false;

    public openedPrintOptionsAccordions: IOpenedPrintOptionsAccordions = {
        consistencyChecksAccordion: false,
        componentsAccordion: false,
        settingsAccordion: false,
        signatureAccordion: false,
        colorsAccordion: false,
        fxAccordion: false,
    };

    //TODO: Remove when adding the complementary reports print preview.
    complementaryReport = new UntypedFormControl({
        value: false,
        disabled: true,
    });
    styleOptions: IStyleOptions | undefined;
    complementaryReportsSelection: ComplementaryReportsSelection = [];

    @Input() set defaultPrintDisplayOptions(displayOptions: IDisplayOptions) {
        if (this._printOptionsSavedData == null) {
            this.initialDisplayOptions = this.mapDisplayOptionsToUserDisplayOptions(
                cloneDeep<IDisplayOptions>(displayOptions),
                this.initialDisplayOptions,
            );

            this.updateFormInitialOptionsValue();
        }
    }

    @Input() set printOptionsSavedData(printOptions: IPrintOptions) {
        if (printOptions != null) {
            this._printOptionsSavedData = printOptions;
            this.refreshComplementaryReportsSelection(false);
            this.updateFormWithSavedData();
        }
    }

    @Input() set complementaryReports(value: IComplementaryReport[] | null) {
        this._complementaryReports = value ?? [];
        this.refreshComplementaryReportsSelection(true);
    }

    get complementaryReports(): IComplementaryReport[] {
        return this._complementaryReports;
    }

    @Input() set mainTitleDefaultValue(value: string | null) {
        if (value != null && value.length > 0) {
            this.initialDisplayOptions.mainTitle = value;
            this.form.get('displayOptions')?.get('mainTitle')?.setValue(value);
        }
    }

    @Input() set subtitleDefaultValue(value: string | null) {
        if (value != null && value.length > 0) {
            this.initialDisplayOptions.subtitle = value;
            this.form.get('displayOptions')?.get('subtitle')?.setValue(value);
        }
    }

    @Input() set previousDateTitleDefaultValue(value: string | null) {
        if (value != null && value.length > 0) {
            this._previousDateTitleDefaultValue = value;
            this.prepareInitialDisplayOptionsDateTitle();
            this.form.get('displayOptions')?.get('dateTitle')?.setValue(this.initialDisplayOptions.dateTitle);
        }
    }

    @Input() set currentDateTitleDefaultValue(value: string | null) {
        if (value != null && value.length > 0 && this._printOptionsSavedData == null) {
            this._currentDateTitleDefaultValue = value;
            this.prepareInitialDisplayOptionsDateTitle();
            this.form.get('displayOptions')?.get('dateTitle')?.setValue(this.initialDisplayOptions.dateTitle);
        }
    }

    @Input() disableShowEmsToggle = true;

    /** FX (REV-21512): shows the Funktionalwährung accordion — Functional-Currency reports only. */
    @Input() set fxEnabled(value: boolean) {
        this._fxEnabled = value;
        this.forceRatiosOffWhileFxColumnsOn();
    }
    get fxEnabled(): boolean {
        return this._fxEnabled;
    }
    private _fxEnabled = false;

    incomeItemsSelectionsForCalculatingRatio: IItemForCalculatingRatioOption[] = [];
    @Input() set incomeItemsOptionsForCalculatingRatio(value: IItemForCalculatingRatioOption[]) {
        if (value.length === EMPTY_ARRAY_LENGTH) {
            return;
        }

        this.incomeItemsSelectionsForCalculatingRatio = value;
        const incomeItemNameForCalculatingRatioFormControlValue = this.form.value.displayOptions
            .incomeItemNameForCalculatingRatio as string | undefined;
        if (incomeItemNameForCalculatingRatioFormControlValue != null) {
            // if user already selected a value, then we handle change in options without clearing his selection (if selection is still valid)
            this.handleChangeInIncomeItemsSelections();
        } else {
            // else we initialize the selection with the first option in the array
            this.initializeIncomeItemDefaultValueForRatioCalculation();
        }
    }

    @Input() printOptionsVisibility: PrintOptionsVisibility | undefined;

    @Input() reportSectionsTitles: IReportSectionsTitles | undefined;

    form: UntypedFormGroup;
    local = localizationKeys;

    mainTitleDefaultText: string;

    selectedIncomeItemForCalculatingRatioFormControl = new UntypedFormControl();

    @Output() printOptions: EventEmitter<IPrintOptions> = new EventEmitter();
    @Output() initialPrintOptions: EventEmitter<IPrintOptions> = new EventEmitter();
    @Output() triggerInitialIncomeItemsOptionsPreparationForRatioCalculation = new EventEmitter();

    @Output()
    incomeItemForCalculatingRatioSelectionChange = new EventEmitter<IItemForCalculatingRatioOption>();

    @Output() exportToWord = new EventEmitter<void>();

    @Output() exportExcel = new EventEmitter<void>();
    @Output() showCompanyLogo = new EventEmitter<void>();
    @Output() printComplementaryReport = new EventEmitter<{ index: number; fileId?: string }>();

    @Input() organizationSettings: OrganizationSettings;

    constructor(
        private fb: UntypedFormBuilder,
        private dateLocalePipe: DateLocalePipe,
        private matDialog: MatDialog,
        private matIconRegistry: MatIconRegistry,
        private domSanitizer: DomSanitizer,
        private readonly appStateDispatcher: AppStateDispatcher,
    ) {
        this.initializeForm();
        this.matIconRegistry.addSvgIcon(
            'aumico-pattern-icon-download',
            this.domSanitizer.bypassSecurityTrustResourceUrl(`/assets/icons/aumico-pattern-icon-download.svg`),
        );
        this.appStateDispatcher
            .getOpenedPrintOptionsAccordions()
            .pipe(
                distinctUntilChanged((previous, current) => fastDeepEqual(previous, current)),
                untilDestroyed(this),
            )
            .subscribe((openedPrintOptionsAccordions) => {
                this.openedPrintOptionsAccordions =
                    cloneDeep<IOpenedPrintOptionsAccordions>(openedPrintOptionsAccordions);
            });
    }

    ngOnInit(): void {
        debug('ngOnInit');
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['organizationSettings'] && changes['organizationSettings'].currentValue) {
            // Update form when organizationSettings changes
            this.patchFormWithOrganizationSettings();
        }
    }

    private patchFormWithOrganizationSettings(): void {
        if (this.organizationSettings && (this.styleOptions == null || this.styleOptions.useCustomColors === false)) {

            this.styleOptions = {
                titleColor: this.organizationSettings.titleColor,
                textColor: this.organizationSettings.textColor,
                backgroundColor: this.organizationSettings.backgroundColor,
                tableHeaderColor: this.organizationSettings.tableHeaderColor,
                tableFooterColor: this.organizationSettings.tableFooterColor,
                backgroundColorPY: this.organizationSettings.backgroundColorPY,
                useCustomColors: false,
            };
            this.form.patchValue({
                styleOptions: this.styleOptions,
            });
        }
    }

    private initializeForm(): void {
        if (this.initialSignatureOptions.date == null) {
            this.initialSignatureOptions.date = this.dateLocalePipe.transform(new Date()).toString();
        }

        this.form = this.fb.group({
            styleOptions: this.createFormFields({
                titleColor: DEFAULT_STYLE_OPTIONS.titleColor,
                textColor: DEFAULT_STYLE_OPTIONS.textColor,
                backgroundColor: DEFAULT_STYLE_OPTIONS.backgroundColor,
                tableHeaderColor: DEFAULT_STYLE_OPTIONS.tableHeaderColor,
                tableFooterColor: DEFAULT_STYLE_OPTIONS.tableFooterColor,
                backgroundColorPY: DEFAULT_STYLE_OPTIONS.backgroundColorPY,
            }),
            displayOptions: this.createFormFields(this.initialDisplayOptions),
            signatureOptions: this.createFormFields(this.initialSignatureOptions),
        });

        this.monitorChanges();
        this.monitorSelectedIncomeItemForCalculatingRatioFormControl();
        // emitting value after initialization incase user didn't do any changes
        this.emitInitialPrintOptionsData();
        const printOptionsToSave: IPrintOptions = {
            ...this.form.value,
            displayOptions: {
                ...this.mapUserDisplayOptionsToDisplayOptionsToSave(this.form.value.displayOptions),
                complementaryReportsSelection: this.complementaryReportsSelection,
            },
        };
        this.printOptions.emit({
            ...printOptionsToSave,
            reportOrder: this.initialReportOrder,
            reportSectionsTitles: this.reportSectionsTitles,
        });
        debug(this.form.get('displayOptions'));
    }

    private updateFormWithSavedData(): void {
        this.initialDisplayOptions = this.mapDisplayOptionsToUserDisplayOptions(
            cloneDeep<IDisplayOptions>(this._printOptionsSavedData.displayOptions),
        );
        this.initialSignatureOptions = cloneDeep<ISignatureOptions>(this._printOptionsSavedData.signatureOptions);
        if (this._printOptionsSavedData.styleOptions.useCustomColors) {
            this.styleOptions = cloneDeep<IStyleOptions>(this._printOptionsSavedData.styleOptions);
            this.form.patchValue({
                styleOptions: this.styleOptions,
            });
        } else if (this.organizationSettings) {
            this.patchFormWithOrganizationSettings();
        }
        this.form
            .get('displayOptions')
            .patchValue(
                this.mapDisplayOptionsToUserDisplayOptions(
                    cloneDeep<IDisplayOptions>(this._printOptionsSavedData.displayOptions),
                ),
            );
        this.form.get('signatureOptions').patchValue(this._printOptionsSavedData.signatureOptions);

        const printOptionsToSave: IPrintOptions = {
            ...this.form.value,
            displayOptions: {
                ...this.mapUserDisplayOptionsToDisplayOptionsToSave(this.form.value.displayOptions),
                complementaryReportsSelection: this.complementaryReportsSelection,
            },
        };

        this.printOptions.emit({
            ...printOptionsToSave,
            reportOrder: this._printOptionsSavedData.reportOrder,
            reportSectionsTitles: this.reportSectionsTitles,
        });
        this.emitInitialPrintOptionsData();
        this.forceRatiosOffWhileFxColumnsOn();
    }

    private updateFormInitialOptionsValue(): void {
        this.form.get('displayOptions').patchValue(this.initialDisplayOptions);
        this.form.get('signatureOptions').patchValue(this.initialSignatureOptions);
        this.form.get('styleOptions').patchValue(this.styleOptions);

        this.emitInitialPrintOptionsData();
    }

    private createFormFields(options: IStyleOptions | IUserDisplayOptions | ISignatureOptions): UntypedFormGroup {
        const formGroup = {};
        Object.keys(options).forEach((key) => {
            formGroup[key] = new UntypedFormControl(options[key]);
        });
        return this.fb.group(formGroup);
    }

    private monitorChanges(): void {
        this.form.valueChanges
            .pipe(
                distinctUntilChanged((previousFormChanges, currentFormChanges) =>
                    fastDeepEqual(previousFormChanges, currentFormChanges),
                ),
                untilDestroyed(this),
            )
            .subscribe((value: IPrintOptions) => {


                const printOptionsToSave: IPrintOptions = {
                    ...this.form.value,
                    styleOptions: { ...value.styleOptions, useCustomColors: this.styleOptions?.useCustomColors },
                    displayOptions: {
                        ...this.mapUserDisplayOptionsToDisplayOptionsToSave(this.form.value.displayOptions),
                        complementaryReportsSelection: this.complementaryReportsSelection,
                    },
                };

                this.printOptions.emit({
                    ...printOptionsToSave,
                    reportOrder: this.initialReportOrder,
                    reportSectionsTitles: this.reportSectionsTitles,
                });
            });
        // some options depend on one another so we need to check & adjust dependencies
        // showCoverSheet Option has 3 dependent options that need to be false if show cover sheet is with false
        this.form
            .get('displayOptions')
            .get('showCoverSheet')
            .valueChanges.pipe(
                filter((showCoverSheet) => !showCoverSheet),
                untilDestroyed(this),
            )
            .subscribe(() => {
                this.form.get('displayOptions').get('showCompanyInfos').setValue(false, { emitEvent: false });
                this.form.get('displayOptions').get('showCompanyLogo').setValue(false, { emitEvent: false });
                this.form.get('displayOptions').get('showUIDonCover').setValue(false, { emitEvent: false });
            });
        // FX (REV-21512): four currency columns leave no room for ratio columns — enabling them
        // forces Kennzahlen OFF (the toggle is additionally [disabled]-locked); disabling the FX
        // columns re-enables the toggle, which deliberately stays off until the user opts back in.
        // Non-FX reports carry the hidden control at its default (true), so the rule must only
        // apply while FX is enabled — otherwise restoring saved options resets showRatios.
        this.form
            .get('displayOptions')
            .get('fxShowStatementDisplayColumns')
            .valueChanges.pipe(
                filter((fxColumnsOn) => fxColumnsOn === true && this.fxEnabled),
                untilDestroyed(this),
            )
            .subscribe(() => {
                this.form.get('displayOptions').get('showRatios').setValue(false, { emitEvent: false });
            });

        // showCompanyLogo has 1 dependent option that needs to be with false if showCompanyLogo is true
        this.form
            .get('displayOptions')
            .get('showCompanyLogo')
            .valueChanges.pipe(
                filter((showCompanyLogo) => showCompanyLogo),
                untilDestroyed(this),
            )
            .subscribe(() => {
                this.form.get('displayOptions').get('showCompanyInfos').setValue(false, { emitEvent: false });
            });

        this.form
            .get('displayOptions')
            .get('showReportCompanyLogo')
            .valueChanges.pipe(
                filter((showCompanyLogo) => showCompanyLogo),
                untilDestroyed(this),
            )
            .subscribe((value) => {
                this.showCompanyLogo.emit(value);
            });

        // showCompanyInfo has 1 dependent option that needs to be with false is showCompanyInfo is with true
        this.form
            .get('displayOptions')
            .get('showCompanyInfos')
            .valueChanges.pipe(
                filter((showCompanyInfos) => showCompanyInfos),
                untilDestroyed(this),
            )
            .subscribe(() => {
                this.form.get('displayOptions').get('showCompanyLogo').setValue(false, { emitEvent: false });
            });

        // showCustomPageNumbers when turned off (false), startAtPage needs to be reset to 1
        this.form
            .get('displayOptions')
            .get('showCustomPageNumbers')
            .valueChanges.pipe(
                filter((showCustomPageNumbers) => !showCustomPageNumbers),
                untilDestroyed(this),
            )
            .subscribe(() => {
                this.form
                    .get('displayOptions')
                    .get('startAtPage')
                    .setValue(START_AT_PAGE_DEFAULT_VALUE, { emitEvent: false });
            });

        this.form.get('styleOptions').valueChanges
            .pipe(untilDestroyed(this))
            .subscribe((styleOptionsChanges) => {
                const printOptionsToSave: IPrintOptions = {
                    ...this.form.value,
                    displayOptions: {
                        ...this.mapUserDisplayOptionsToDisplayOptionsToSave(this.form.value.displayOptions),
                        complementaryReportsSelection: this.complementaryReportsSelection,
                    },
                };

                const styleOptionsChangesClean = Object.entries(styleOptionsChanges).reduce((acc, [key, value]) => {
                    if (value !== undefined) {
                        acc[key] = value;
                    }
                    return acc;
                }, {});

                if (isEqual(this.organizationSettings, styleOptionsChangesClean) && (this.styleOptions == null || !this.styleOptions.useCustomColors)) {
                    printOptionsToSave.styleOptions.useCustomColors = false;

                } else {
                    printOptionsToSave.styleOptions.useCustomColors = true;
                }

                if (this.styleOptions) {
                    this.styleOptions.useCustomColors = printOptionsToSave.styleOptions.useCustomColors;
                }

                this.printOptions.emit({
                    ...printOptionsToSave,
                    reportOrder: this.initialReportOrder,
                    reportSectionsTitles: this.reportSectionsTitles,
                });
            });
    }

    onShowComplementaryReportsToggleChanged(showComplementaryReports: boolean): void {
        this.setComplementaryReportsSelection(showComplementaryReports);
        this.emitPrintOptionsWithComplementaryReports();

    }

    private monitorSelectedIncomeItemForCalculatingRatioFormControl(): void {
        this.selectedIncomeItemForCalculatingRatioFormControl.valueChanges
            .pipe(untilDestroyed(this))
            .subscribe((selectedIncomeRatioReferenceOption) => {
                this.form
                    .get('displayOptions')
                    .get('incomeItemNameForCalculatingRatio')
                    .setValue(selectedIncomeRatioReferenceOption.name);
                this.incomeItemForCalculatingRatioSelectionChange.emit(selectedIncomeRatioReferenceOption);
            });
    }

    private handleChangeInIncomeItemsSelections(): void {
        const incomeItemNameForCalculatingRatioFormControlValue: string | undefined =
            this.form.value.displayOptions.incomeItemNameForCalculatingRatio;
        const incomeItemOptionForCalculatingRatio = this.getIncomeItemOptionByIncomeItemName(
            incomeItemNameForCalculatingRatioFormControlValue,
        );
        if (incomeItemOptionForCalculatingRatio == null) {
            //incomeItemOptionForCalculatingRatio will be null in the case of the item which was previously selected but now has been omitted from the options due to having closing or opening with 0
            // so we reset to default which is the first item in the array of options
            const defaultIncomeItemSelectionForCalculatingRatio = this.getDefaultIncomeItemOption();
            this.selectedIncomeItemForCalculatingRatioFormControl.setValue(
                defaultIncomeItemSelectionForCalculatingRatio,
            );
        } else {
            this.selectedIncomeItemForCalculatingRatioFormControl.setValue(incomeItemOptionForCalculatingRatio);
        }

        this.incomeItemForCalculatingRatioSelectionChange.emit(
            this.selectedIncomeItemForCalculatingRatioFormControl.value,
        );
    }

    private emitInitialPrintOptionsData(): void {
        const printOptionsToSave: IPrintOptions = {
            displayOptions: {
                ...this.mapUserDisplayOptionsToDisplayOptionsToSave(this.form.value.displayOptions),
                complementaryReportsSelection: this.complementaryReportsSelection,
            },
            signatureOptions: this.initialSignatureOptions,
            styleOptions: this.styleOptions,
            reportOrder: this.initialReportOrder,
        };
        this.initialPrintOptions.emit(printOptionsToSave);
        if (
            this.incomeItemsSelectionsForCalculatingRatio.length === EMPTY_ARRAY_LENGTH &&
            this.initialDisplayOptions.showRatios
        ) {
            this.triggerInitialIncomeItemsOptionsPreparationForRatioCalculation.emit();
        }
    }

    private prepareInitialDisplayOptionsDateTitle(): void {
        if (this._previousDateTitleDefaultValue == null || this._currentDateTitleDefaultValue == null) {
            return;
        }

        this.initialDisplayOptions.dateTitle =
            this._previousDateTitleDefaultValue + '-' + this._currentDateTitleDefaultValue;
    }

    private getIncomeItemOptionByIncomeItemName(itemName: string): IItemForCalculatingRatioOption | undefined {
        return this.incomeItemsSelectionsForCalculatingRatio.find((option) => option.name === itemName);
    }

    private getDefaultIncomeItemOption(): IItemForCalculatingRatioOption | undefined {
        const incomeItemNameForCalculatingRatioFormControl = this.form
            .get('displayOptions')
            .get('incomeItemNameForCalculatingRatio');
        if (
            incomeItemNameForCalculatingRatioFormControl == null ||
            this.incomeItemsSelectionsForCalculatingRatio.length === EMPTY_ARRAY_LENGTH
        ) {
            return undefined;
        }

        const matchingOption = this.incomeItemsSelectionsForCalculatingRatio[FIRST_ELEMENT_INDEX];
        incomeItemNameForCalculatingRatioFormControl.setValue(matchingOption.name);
        // alert user of change in selected income item for calculating ratio
        const popupData: IPopupData = {
            titleLocalizationKey: this.local.aumico_information_dialog_title,
            isContentHtmlType: false,
            contentMessage:
                this.local.aumico_annual_report_print_options_income_ratio_reference_internal_change_warning_message,
        };

        this.matDialog.open<AumicoInformationPopupComponent, IPopupData, void>(AumicoInformationPopupComponent, {
            width: '80%',
            data: popupData,
            panelClass: MAT_DIALOG_CUSTOM_CLASS,
        });

        return matchingOption;
    }

    compareIncomeItemForCalculatingRatio(
        item1: IItemForCalculatingRatioOption,
        item2: IItemForCalculatingRatioOption,
    ): boolean {
        return item1.name === item2.name;
    }

    private mapDisplayOptionsToUserDisplayOptions(
        displayOptions: IDisplayOptions,
        oldDisplayOptions?: IUserDisplayOptions,
    ): IUserDisplayOptions {
        const hideEmptyLinesValue = displayOptions.hideEmptyLines;

        const intermediateDisplayOptions = omit<IDisplayOptions, 'hideDecimalPlaces' | 'hideEmptyLines'>(
            displayOptions,
            'hideDecimalPlaces',
            'hideEmptyLines',
        );

        return {
            ...intermediateDisplayOptions,
            showEmptyLines: !hideEmptyLinesValue,
            mainTitle: oldDisplayOptions?.mainTitle ?? displayOptions.mainTitle,
            subtitle: oldDisplayOptions?.subtitle ?? displayOptions.subtitle,
            dateTitle: oldDisplayOptions?.dateTitle ?? displayOptions.dateTitle,
        } as IUserDisplayOptions;
    }

    private mapUserDisplayOptionsToDisplayOptionsToSave(userDisplayOptions: IUserDisplayOptions): IDisplayOptions {
        const showDecimalPlacesValue = userDisplayOptions.showDecimalPlaces;
        const showEmptyLinesValue = userDisplayOptions.showEmptyLines;

        const intermediateDisplayOptions = omit<IUserDisplayOptions, 'showDecimalPlaces' | 'showEmptyLines'>(
            userDisplayOptions,
            'showDecimalPlaces',
            'showEmptyLines',
        );

        return {
            ...intermediateDisplayOptions,
            hideDecimalPlaces: !showDecimalPlacesValue,
            hideEmptyLines: !showEmptyLinesValue,
        } as IDisplayOptions;
    }

    private initializeIncomeItemDefaultValueForRatioCalculation(): void {
        // As an initial value, the first option is selected by default
        this.initialDisplayOptions.incomeItemNameForCalculatingRatio =
            this.incomeItemsSelectionsForCalculatingRatio[FIRST_ELEMENT_INDEX].name;
        this.selectedIncomeItemForCalculatingRatioFormControl.setValue(
            this.incomeItemsSelectionsForCalculatingRatio[FIRST_ELEMENT_INDEX],
        );
        this.emitInitialPrintOptionsData();
        this.incomeItemForCalculatingRatioSelectionChange.emit(
            this.incomeItemsSelectionsForCalculatingRatio[FIRST_ELEMENT_INDEX],
        );
    }

    protected onExportToWord(): void {
        this.exportToWord.emit();
    }

    protected onExportExcel() {
        this.exportExcel.emit();
    }

    public isComplementaryReportSelected(index: number): boolean {
        if (!this.form?.value?.displayOptions?.showComplementaryReports) {
            return false;
        }
        const selection = this.complementaryReportsSelection.find((item) => item.index === index);
        return selection?.includeInReport ?? true;
    }

    public onComplementaryReportToggleChanged(index: number, includeInReport: boolean): void {
        if (!this.form?.value?.displayOptions?.showComplementaryReports) {
            return;
        }
        const selectionIndex = this.complementaryReportsSelection.findIndex((item) => item.index === index);
        if (selectionIndex >= 0) {
            this.complementaryReportsSelection[selectionIndex] = {
                ...this.complementaryReportsSelection[selectionIndex],
                includeInReport,
            };
        } else {
            this.complementaryReportsSelection.push({
                index,
                fileId: this._complementaryReports[index]?.fileId,
                includeInReport,
            });
        }

        this.emitPrintOptionsWithComplementaryReports();
    }

    public onPrintComplementaryReport(index: number): void {
        this.printComplementaryReport.emit({
            index,
            fileId: this._complementaryReports[index]?.fileId,
        });
    }

    public onToggleDesignSettingsAccordion(): void {
        this.isDesignSettingsAccordionExpanded = !this.isDesignSettingsAccordionExpanded;
    }

    public onToggleComponentsAccordion(): void {
        this.isComponentsAccordionExpanded = !this.isComponentsAccordionExpanded;
        this.openedPrintOptionsAccordions.componentsAccordion = this.isComponentsAccordionExpanded;
        this.appStateDispatcher.setOpenedPrintOptionsAccordions(this.openedPrintOptionsAccordions);
    }

    public onToggleSettingsAccordion(): void {
        this.isSettingsAccordionExpanded = !this.isSettingsAccordionExpanded;
        this.openedPrintOptionsAccordions.settingsAccordion = this.isSettingsAccordionExpanded;
        this.appStateDispatcher.setOpenedPrintOptionsAccordions(this.openedPrintOptionsAccordions);
    }

    public onToggleSignatureAccordion(): void {
        this.isSignatureAccordionExpanded = !this.isSignatureAccordionExpanded;
        this.openedPrintOptionsAccordions.signatureAccordion = this.isSignatureAccordionExpanded;
        this.appStateDispatcher.setOpenedPrintOptionsAccordions(this.openedPrintOptionsAccordions);
    }

    public onToggleColorsAccordion(): void {
        this.isColorsAccordionExpanded = !this.isColorsAccordionExpanded;
        this.openedPrintOptionsAccordions.colorsAccordion = this.isColorsAccordionExpanded;
        this.appStateDispatcher.setOpenedPrintOptionsAccordions(this.openedPrintOptionsAccordions);
    }

    /**
     * FX (REV-21512): ratio columns don't fit next to four currency columns — the Kennzahlen
     * toggle is locked (and its disabled control drops out of the payload, i.e. ratios print off)
     * while the BS/ER display-currency columns are on. Re-enabling restores the previous choice.
     */
    get fxStatementColumnsOn(): boolean {
        return (
            this.fxEnabled &&
            this.form?.get('displayOptions')?.get('fxShowStatementDisplayColumns')?.value === true
        );
    }

    /** Saved states may carry showRatios=true from before the lock — normalize to OFF while locked. */
    private forceRatiosOffWhileFxColumnsOn(): void {
        const ratios = this.form?.get('displayOptions')?.get('showRatios');
        if (this.fxStatementColumnsOn && ratios?.value === true) {
            ratios.setValue(false, { emitEvent: false });
        }
    }

    public onToggleFxAccordion(): void {
        this.isFxAccordionExpanded = !this.isFxAccordionExpanded;
        this.openedPrintOptionsAccordions.fxAccordion = this.isFxAccordionExpanded;
        this.appStateDispatcher.setOpenedPrintOptionsAccordions(this.openedPrintOptionsAccordions);
    }

    onResetColors(): void {
        if (this.organizationSettings && this.styleOptions) {
            this.styleOptions.useCustomColors = false;
            this.form.patchValue({
                styleOptions: {
                    titleColor: this.organizationSettings.titleColor,
                    textColor: this.organizationSettings.textColor,
                    backgroundColor: this.organizationSettings.backgroundColor,
                    tableHeaderColor: this.organizationSettings.tableHeaderColor,
                    tableFooterColor: this.organizationSettings.tableFooterColor,
                    backgroundColorPY: this.organizationSettings.backgroundColorPY,
                },
            });
        }
    }

    private refreshComplementaryReportsSelection(shouldEmit: boolean): void {
        if (this._complementaryReports.length === EMPTY_ARRAY_LENGTH) {
            this.complementaryReportsSelection = [];
            return;
        }

        const savedSelections = this._printOptionsSavedData?.displayOptions?.complementaryReportsSelection ?? [];
        const existingSelections = this.complementaryReportsSelection;

        this.complementaryReportsSelection = this._complementaryReports.map((report, index) => {
            const selection =
                this.findComplementaryReportSelection(savedSelections, report, index) ??
                this.findComplementaryReportSelection(existingSelections, report, index);

            return {
                index,
                fileId: report?.fileId,
                includeInReport: selection?.includeInReport ?? true,
            };
        });

        if (this.form?.value?.displayOptions?.showComplementaryReports === false) {
            this.setComplementaryReportsSelection(false);
        }

        if (shouldEmit && this.form != null) {
            this.emitPrintOptionsWithComplementaryReports();
        }
    }

    private setComplementaryReportsSelection(includeInReport: boolean): void {
        this.complementaryReportsSelection = this._complementaryReports.map((report, index) => ({
            index,
            fileId: report?.fileId,
            includeInReport,
        }));
    }

    private findComplementaryReportSelection(
        selections: ComplementaryReportsSelection | undefined,
        report: IComplementaryReport | undefined,
        index: number,
    ): ComplementaryReportSelectionItem | undefined {
        if (selections == null || selections.length === EMPTY_ARRAY_LENGTH) {
            return undefined;
        }

        if (report?.fileId) {
            const byFileId = selections.find((item) => item.fileId === report.fileId);
            if (byFileId) {
                return byFileId;
            }
        }

        return selections.find((item) => item.index === index);
    }

    private emitPrintOptionsWithComplementaryReports(): void {
        const printOptionsToSave: IPrintOptions = {
            ...this.form.value,
            styleOptions: { ...this.form.value.styleOptions, useCustomColors: this.styleOptions?.useCustomColors },
            displayOptions: {
                ...this.mapUserDisplayOptionsToDisplayOptionsToSave(this.form.value.displayOptions),
                complementaryReportsSelection: this.complementaryReportsSelection,
            },
        };

        this.printOptions.emit({
            ...printOptionsToSave,
            reportOrder: this._printOptionsSavedData?.reportOrder ?? this.initialReportOrder,
            reportSectionsTitles: this.reportSectionsTitles,
        });
    }
}
